import { Box, Card, CardContent, CircularProgress, Grid, InputAdornment } from "@material-ui/core";
import * as React from "react";
import {
  BooleanInput,
  Button,
  Create,
  FormWithRedirect,
  SaveButton,
  TextInput,
  Toolbar,
  maxLength,
  required,
  useNotify,
} from "react-admin";
import { useForm } from "react-final-form";
import { useHistory } from "react-router-dom";
import { useVerifyPermission } from "@airborne/react-admin";
import CustomerSelect from "../../../Components/CustomerSelect/CustomerSelect";
import { useAppDataProvider } from "../../../dataprovider";

const PnDescriptionAutoFill = ({ isLocked, isLoading }: { isLocked: boolean; isLoading: boolean }) => {
  return (
    <TextInput
      source="pnDescription"
      label="Description"
      validate={[required(), maxLength(250)]}
      fullWidth
      variant="outlined"
      disabled={isLocked || isLoading}
      InputProps={{
        endAdornment: isLoading ? (
          <InputAdornment position="end">
            <CircularProgress size={18} />
          </InputAdornment>
        ) : undefined,
      }}
    />
  );
};

const LocalRepairComponentCreate = props => {
  useVerifyPermission("localrepaircomponents", "create");
  const history = useHistory();
  const notify = useNotify();
  const dataProvider = useAppDataProvider();
  const [isDescriptionLocked, setIsDescriptionLocked] = React.useState(false);
  const [isPnLoading, setIsPnLoading] = React.useState(false);
  const lastQueriedPn = React.useRef<string | null>(null);

  const onSuccess = () => {
    notify("Local Repair Component created successfully.", "success");
    history.push("/localrepaircomponents");
  };

  const onFailure = (error: any) => {
    notify(error?.message ?? "Failed to create Local Repair Component.", "error");
  };

  const handlePnBlur = (form: any) => async (e: React.FocusEvent<any>) => {
    const pnValue = e.target.value?.trim();
    if (!pnValue || pnValue === lastQueriedPn.current) return;
    setIsPnLoading(true);
    lastQueriedPn.current = pnValue;
    try {
      const response: any = await dataProvider.getPnMasterByPn(pnValue);
      if (response?.data?.pnDescription) {
        form.change("pnDescription", response.data.pnDescription);
        setIsDescriptionLocked(true);
      } else {
        setIsDescriptionLocked(false);
      }
    } catch {
      setIsDescriptionLocked(false);
    } finally {
      setIsPnLoading(false);
    }
  };

  return (
    <Create
      title="Create Local Repair Component"
      component="div"
      onSuccess={onSuccess}
      onFailure={onFailure}
      {...props}
    >
      <FormWithRedirect
        {...props}
        render={(formProps: any) => {
          // eslint-disable-next-line react-hooks/rules-of-hooks
          const form = useForm();
          return (
            <Card>
              <form onSubmit={e => e.preventDefault()}>
                <CardContent>
                  <Grid container spacing={2} direction="row">
                    <Grid item xs={12} sm={6} md={4} lg={4}>
                      <CustomerSelect
                        source="ownerCode"
                        optionText="ownerCode"
                        optionValue="ownerCode"
                        label="Owner Code"
                        validate={required()}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={4}>
                      <TextInput
                        source="pn"
                        label="PN"
                        validate={[required(), maxLength(50)]}
                        fullWidth
                        variant="outlined"
                        onBlur={handlePnBlur(form)}
                        onChange={() => {
                          setIsDescriptionLocked(false);
                          form.change("pnDescription", "");
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={4}>
                      <PnDescriptionAutoFill isLocked={isDescriptionLocked} isLoading={isPnLoading} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={4}>
                      <TextInput
                        source="vendor"
                        label="Vendor / Local Company"
                        validate={[required(), maxLength(200)]}
                        fullWidth
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={4}>
                      <Box mt={1}>
                        <BooleanInput source="isActive" label="Is Active" defaultValue={true} />
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
                <Toolbar>
                  <SaveButton
                    saving={formProps.saving}
                    disabled={formProps.invalid || formProps.saving || isPnLoading}
                    handleSubmitWithRedirect={formProps.handleSubmitWithRedirect}
                  />
                  <Button label="Back to List" onClick={() => history.push("/localrepaircomponents")} />
                </Toolbar>
              </form>
            </Card>
          );
        }}
      />
    </Create>
  );
};

export default LocalRepairComponentCreate;
